import { useState, useMemo } from "react";
import { Hammer, Plus, Trash2, Layers, TrendingUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { isRestrictedUser } from "../../pages/Login";
import {
  GESTES_ISO_LABELS, COUT_UNITAIRE, calc174, calcMPR, calcAutoQuantites174
} from "@/utils/simulateurUtils";

const fc = (v) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v || 0);

const TRAVAUX_TYPES = [
  { key: "isolation_combles", label: "Isolation des combles", unit: "m²", geste: "combles" },
  { key: "isolation_plancher", label: "Isolation plancher bas", unit: "m²", geste: "planchersBas" },
  { key: "isolation_murs_int", label: "Isolation murs intérieurs", unit: "m²", geste: "murs" },
  { key: "isolation_murs_ext", label: "Isolation murs extérieurs", unit: "m²" },
  { key: "isolation_rampants", label: "Rampants de toiture", unit: "m²", geste: "rampant" },
  { key: "isolation_toiture_terrasse", label: "Isolation toiture terrasse", unit: "m²" },
  { key: "fenetre", label: "Remplacement fenêtres", unit: "unités", geste: "fenetres" },
  { key: "chauffe_eau", label: "Chauffe-eau thermodynamique", unit: "unités" },
  { key: "pac_air_eau", label: "PAC air/eau", unit: "unités" },
  { key: "pac_ssc", label: "PAC/SSC", unit: "unités" },
  { key: "vmc", label: "VMC double flux", unit: "unités" },
  { key: "cesi", label: "CESI", unit: "unités" },
  { key: "monoSplit", label: "Mono Split", unit: "unités" },
  { key: "radiateurs", label: "Radiateurs électriques", unit: "unités" },
  { key: "vmcSimple", label: "VMC Simple flux", unit: "unités" },
];

export default function TravauxTab({ dossier, onSave, is174, formatCurrency }) {
  const [activeLogement, setActiveLogement] = useState("all");
  const isRestricted = isRestrictedUser();
  const fmt = formatCurrency || fc;

  const simData = dossier?.simulation_data;
  const hasDetailSurfaces = simData?.multiLogement && simData?.multiLogementInfo?.surfaceMode === "detail";
  const nbLog = simData?.multiN || dossier?.nombre_logements || 1;
  const travauxData = dossier?.travaux_realises || {};

  // Merge custom keys
  const existingKeys = new Set(TRAVAUX_TYPES.map(t => t.key));
  const customKeys = Object.keys(travauxData).filter(k => !existingKeys.has(k));
  const allTypes = [
    ...TRAVAUX_TYPES,
    ...customKeys.map(k => ({ key: k, label: travauxData[k]?.label || k.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()), unit: "unités" }))
  ];

  // Calculate totals
  const totalMarchandise = Object.values(travauxData).reduce((s, t) => s + (t?.selected ? (t.cout_marchandise || 0) : 0), 0);
  const totalPose = Object.values(travauxData).reduce((s, t) => s + (t?.selected ? (t.cout_pose || 0) : 0), 0);
  const totalTravaux = totalMarchandise + totalPose;

  // Dynamic margin recalculation based on actual travaux
  const margeCalculation = useMemo(() => {
    if (!simData) {
      // Fallback: simple aide - travaux
      const aides = (dossier?.mpr_calcule || 0) + (dossier?.cee_calcule || 0);
      return { margeEstimee: aides - totalTravaux, totalAides: aides, mprCalc: dossier?.mpr_calcule || 0, ceeCalc: dossier?.cee_calcule || 0, detail: null };
    }

    const programme = dossier?.programme || simData?.programme_selectionne || "174";
    let marge174 = 0, margeMpr = 0, cee174 = 0, ceeMpr = 0, mprAide = 0;

    // Recalc 174 using actual travaux costs
    if ((programme === "174" || programme === "cumule") && simData.elig174?.eligible) {
      const activeResult = simData.active174Result;
      if (activeResult) {
        // Use stored CEE value but adjust margin based on actual travaux cost
        cee174 = activeResult.caTTC || activeResult.cee || 0;
        const caHT = cee174 / 1.055;
        marge174 = caHT - totalTravaux;
      }
    }

    // Recalc MPR
    if ((programme === "MPR" || programme === "mpr" || programme === "cumule") && simData.eligMPR?.eligible) {
      const activeResult = simData.activeMPRResult;
      if (activeResult) {
        mprAide = activeResult.aideMPR || activeResult.mpr || 0;
        ceeMpr = activeResult.cee || 0;
        // MPR margin = aides - actual travaux costs (for MPR portion)
        if (programme !== "cumule") {
          margeMpr = (mprAide + ceeMpr) - totalTravaux;
        } else {
          margeMpr = activeResult.marge || 0; // Keep original for cumule since 174 handles costs
        }
      }
    }

    const totalAides = mprAide + cee174 + ceeMpr;
    const margeEstimee = programme === "cumule" ? marge174 + margeMpr : programme === "174" ? marge174 : margeMpr;

    return {
      margeEstimee: Math.round(margeEstimee),
      totalAides: Math.round(totalAides),
      mprCalc: Math.round(mprAide),
      ceeCalc: Math.round(cee174 + ceeMpr),
      marge174: Math.round(marge174),
      margeMpr: Math.round(margeMpr),
      detail: programme,
    };
  }, [simData, dossier, totalTravaux]);

  const saveTravaux = async (newTravaux) => {
    try {
      // Also update marge_prevue with the recalculated margin
      await onSave({ travaux_realises: newTravaux });
    } catch (err) { /* handled by parent */ }
  };

  const updateField = (key, field, value) => {
    const newTravaux = { ...travauxData, [key]: { ...travauxData[key], [field]: value } };
    saveTravaux(newTravaux);
  };

  const toggleSelected = (key, checked) => {
    const newTravaux = { ...travauxData, [key]: { ...travauxData[key], selected: checked } };
    saveTravaux(newTravaux);
  };

  const addCustomWork = () => {
    const name = prompt("Nom du poste de travail à ajouter :");
    if (!name) return;
    const key = name.toLowerCase().replace(/[^a-z0-9]/g, '_');
    saveTravaux({ ...travauxData, [key]: { selected: true, quantite: 1, cout_marchandise: 0, cout_pose: 0, label: name } });
  };

  const removeWork = (key) => {
    const newTravaux = { ...travauxData };
    delete newTravaux[key];
    saveTravaux(newTravaux);
  };

  const renderWorkItem = (travail) => {
    const isSelected = travauxData[travail.key]?.selected;
    const qty = travauxData[travail.key]?.quantite || 0;
    const coutMarchandise = travauxData[travail.key]?.cout_marchandise || 0;
    const coutPose = travauxData[travail.key]?.cout_pose || 0;
    const isCustom = !existingKeys.has(travail.key);
    const subtotal = coutMarchandise + coutPose;

    return (
      <div key={travail.key}
        className={cn("border rounded-xl p-4 transition-all",
          isSelected ? (is174 ? "border-emerald-500/40 bg-emerald-500/10" : "border-blue-500/40 bg-blue-500/10") : "border-border bg-muted/20"
        )}
        data-testid={`travail-${travail.key}`}
      >
        <div className="flex items-center justify-between mb-2">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={!!isSelected}
              onChange={(e) => toggleSelected(travail.key, e.target.checked)}
              className="w-4 h-4 rounded border-2 accent-emerald-500"
            />
            <span className="text-sm font-semibold">{travauxData[travail.key]?.label || travail.label}</span>
          </label>
          <div className="flex items-center gap-2">
            {isSelected && subtotal > 0 && (
              <span className="text-xs font-medium text-muted-foreground">{fmt(subtotal)}</span>
            )}
            {isCustom && (
              <button onClick={() => removeWork(travail.key)} className="text-red-400 hover:text-red-600">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>
        {isSelected && (
          <div className="grid grid-cols-3 gap-2 mt-2">
            <div className="space-y-1">
              <label className="text-[10px] text-muted-foreground uppercase">Qté ({travail.unit})</label>
              <Input type="number" value={qty || ""} min="0"
                onChange={(e) => updateField(travail.key, 'quantite', parseFloat(e.target.value) || 0)}
                className="h-8 text-xs rounded-lg" />
            </div>
            <div className="space-y-1">
              <label className="text-[10px] text-muted-foreground uppercase">Marchandise</label>
              <Input type="number" value={coutMarchandise || ""} min="0"
                onChange={(e) => updateField(travail.key, 'cout_marchandise', parseFloat(e.target.value) || 0)}
                className="h-8 text-xs rounded-lg" />
            </div>
            <div className="space-y-1">
              <label className="text-[10px] text-muted-foreground uppercase">Pose</label>
              <Input type="number" value={coutPose || ""} min="0"
                onChange={(e) => updateField(travail.key, 'cout_pose', parseFloat(e.target.value) || 0)}
                className="h-8 text-xs rounded-lg" />
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="border border-border rounded-sm bg-card p-6" data-testid="travaux-tab-content">
      <h2 className="font-heading font-semibold text-lg mb-4 flex items-center gap-2">
        <Hammer className={cn("w-5 h-5", is174 ? "text-[hsl(145,100%,45%)]" : "text-primary")} />
        Travaux réalisés
      </h2>
      <p className="text-sm text-muted-foreground mb-4">
        Sélectionnez et modifiez les travaux. Les marges sont recalculées automatiquement.
      </p>

      {/* Per-logement tabs */}
      {hasDetailSurfaces && nbLog > 1 ? (
        <Tabs value={activeLogement} onValueChange={setActiveLogement} className="mb-4">
          <TabsList className="rounded-lg">
            <TabsTrigger value="all" className="rounded-lg text-xs">
              <Layers className="w-3.5 h-3.5 mr-1" /> Tous ({nbLog})
            </TabsTrigger>
            {Array.from({ length: nbLog }, (_, i) => {
              const surf = simData.multiLogementInfo.surfacesDetail?.[i] || "?";
              return (
                <TabsTrigger key={i} value={`log-${i}`} className="rounded-lg text-xs">
                  L{i + 1} ({surf}m²)
                </TabsTrigger>
              );
            })}
          </TabsList>
          <div className="mt-2 text-xs text-muted-foreground">
            {activeLogement === "all"
              ? "Vue agrégée de tous les logements"
              : `Logement ${parseInt(activeLogement.replace("log-", "")) + 1} — ${simData.multiLogementInfo.surfacesDetail?.[parseInt(activeLogement.replace("log-", ""))] || "?"}m²`
            }
          </div>
        </Tabs>
      ) : nbLog > 1 && (
        <div className="mb-4 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg text-sm text-amber-800">
          <Layers className="w-4 h-4 inline mr-1" />
          Multi-logements ({nbLog} logements) — Les travaux sont agrégés pour l'ensemble.
        </div>
      )}

      {/* Travaux grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {allTypes.map(renderWorkItem)}
      </div>

      {/* Add custom work */}
      <div className="mt-3">
        <Button variant="outline" size="sm" onClick={addCustomWork} className="rounded-lg text-xs" data-testid="add-custom-work">
          <Plus className="w-3.5 h-3.5 mr-1" /> Ajouter un poste
        </Button>
      </div>

      {/* Summary with dynamic margin */}
      <div className="mt-6 border-t border-border pt-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <div className="text-center p-3 bg-muted/50 rounded-xl">
            <p className="text-xs text-muted-foreground">Marchandise</p>
            <p className="font-bold text-lg">{fmt(totalMarchandise)}</p>
          </div>
          <div className="text-center p-3 bg-muted/50 rounded-xl">
            <p className="text-xs text-muted-foreground">Pose</p>
            <p className="font-bold text-lg">{fmt(totalPose)}</p>
          </div>
          <div className="text-center p-3 bg-slate-100 rounded-xl border border-slate-200">
            <p className="text-xs text-slate-600 font-semibold">Total travaux</p>
            <p className="font-extrabold text-lg text-slate-700">{fmt(totalTravaux)}</p>
          </div>
          {!isRestricted && (
            <div className={cn("text-center p-3 rounded-xl border",
              margeCalculation.margeEstimee >= 0 ? "bg-emerald-500/10 border-emerald-500/30" : "bg-red-500/10 border-red-500/30"
            )}>
              <p className={cn("text-xs font-semibold", margeCalculation.margeEstimee >= 0 ? "text-emerald-700" : "text-red-700")}>
                Marge estimée
              </p>
              <p className={cn("font-extrabold text-lg", margeCalculation.margeEstimee >= 0 ? "text-emerald-700" : "text-red-700")}>
                {fmt(margeCalculation.margeEstimee)}
              </p>
            </div>
          )}
        </div>

        {/* Detailed margin breakdown */}
        {!isRestricted && (
          <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
            <div className="p-2 bg-muted/30 rounded-lg text-center">
              <p className="text-muted-foreground">MPR</p>
              <p className="font-semibold">{fmt(margeCalculation.mprCalc)}</p>
            </div>
            <div className="p-2 bg-muted/30 rounded-lg text-center">
              <p className="text-muted-foreground">CEE</p>
              <p className="font-semibold">{fmt(margeCalculation.ceeCalc)}</p>
            </div>
            <div className="p-2 bg-muted/30 rounded-lg text-center">
              <p className="text-muted-foreground">Total aides</p>
              <p className="font-semibold">{fmt(margeCalculation.totalAides)}</p>
            </div>
            <div className="p-2 bg-muted/30 rounded-lg text-center">
              <p className="text-muted-foreground">Coût travaux</p>
              <p className="font-semibold">{fmt(totalTravaux)}</p>
            </div>
          </div>
        )}

        {/* Cumule detail */}
        {!isRestricted && margeCalculation.detail === "cumule" && (
          <div className="mt-2 flex gap-2 text-xs">
            <div className="flex-1 p-2 bg-emerald-500/10 rounded-lg text-center border border-emerald-500/30">
              <p className="text-emerald-600">Marge 174</p>
              <p className="font-bold text-emerald-700">{fmt(margeCalculation.marge174)}</p>
            </div>
            <div className="flex-1 p-2 bg-blue-500/10 rounded-lg text-center border border-blue-500/30">
              <p className="text-blue-600">Marge MPR</p>
              <p className="font-bold text-blue-700">{fmt(margeCalculation.margeMpr)}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
